import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faXmark, faThumbsUp, faComment, faShare } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { ValidatePostUrlResponse } from "../schema/liPost";

interface PostPreviewCardProps {
	data: ValidatePostUrlResponse;
	onDismiss: () => void;
}

export const PostPreviewCard: FunctionComponent<PostPreviewCardProps> = ({ data, onDismiss }) => {
	const { t } = useTranslation();
	const [imageError, setImageError] = useState(false);

	const initials = (data.actorName || "?")
		.split(" ")
		.map((w) => w.charAt(0))
		.join("")
		.toUpperCase()
		.slice(0, 2);

	const formattedDate = data.postedAt
		? new Date(data.postedAt).toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			})
		: null;

	return (
		<div className="relative rounded-lg border border-border-light bg-brand-white p-4 mb-4">
			<button
				className="absolute top-2 right-2 text-text-meta hover:text-text p-1"
				onClick={onDismiss}
			>
				<Icon icon={faXmark} />
			</button>

			<div className="text-xs font-semibold text-text-meta mb-3 uppercase">
				{t("liPost.postPreview")}
			</div>

			{/* Author row */}
			<div className="flex items-center gap-3 mb-3">
				{data.actorImage && !imageError ? (
					<img
						alt={data.actorName || ""}
						className="w-10 h-10 rounded-full object-cover"
						referrerPolicy="no-referrer"
						src={data.actorImage}
						onError={() => setImageError(true)}
					/>
				) : (
					<div className="w-10 h-10 rounded-full bg-blue-action text-white flex items-center justify-center text-sm font-semibold">
						{initials}
					</div>
				)}
				<div className="flex flex-col min-w-0">
					{data.actorProfileUrl ? (
						<a
							className="text-sm font-semibold text-text hover:underline truncate"
							href={data.actorProfileUrl}
							rel="noopener noreferrer"
							target="_blank"
						>
							{data.actorName}
						</a>
					) : (
						<span className="text-sm font-semibold text-text truncate">{data.actorName}</span>
					)}
					{data.actorDescription && (
						<span className="text-xs text-text-meta truncate">{data.actorDescription}</span>
					)}
					{formattedDate && <span className="text-xs text-text-meta">{formattedDate}</span>}
				</div>
			</div>

			{/* Post content */}
			{data.commentary && (
				<p className="text-sm text-text mb-3 line-clamp-4 whitespace-pre-line">{data.commentary}</p>
			)}

			{/* Engagement metrics */}
			<div className="flex items-center gap-4 pt-3 border-t border-border-light text-xs text-text-meta">
				<span className="flex items-center gap-1">
					<Icon icon={faThumbsUp} />
					{data.numLikes.toLocaleString()}
				</span>
				<span className="flex items-center gap-1">
					<Icon icon={faComment} />
					{data.numComments.toLocaleString()}
				</span>
				<span className="flex items-center gap-1">
					<Icon icon={faShare} />
					{data.numShares.toLocaleString()}
				</span>
			</div>
		</div>
	);
};
